import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import {
  addToExpandedConceptIds,
  removeFromExpandedConceptIds,
  clearAreAllConceptsExpanded,
  clearCreateAnnotationError,
  clearEditAnnotationError,
} from '../../state/annotationSlice';
import {
  selectAreAllConceptsExpanded,
  selectConceptAnnotationIdsInEditMode,
  selectExpandedConceptIds,
} from '../../state/selector';
import type { Concept } from '../../types/response';

interface UseConceptCardProps {
  concept: Concept;
  annotationCount: number;
}

interface UseConceptCardResults {
  getAnnotationCountSaved: number;
  isExpanded: boolean;
  handleSetIsExpanded: (isExpanded: boolean) => void;
}

export const useConceptCard = ({
  concept,
  annotationCount,
}: UseConceptCardProps): UseConceptCardResults => {
  const areAllConceptsExpanded = useAppSelector(selectAreAllConceptsExpanded);
  const expandedConceptIds = useAppSelector(selectExpandedConceptIds);
  const isExpanded = expandedConceptIds.includes(concept.conceptId);
  const dispatch = useAppDispatch();

  const handleSetIsExpanded = (isExpanded: boolean) => {
    if (isExpanded) {
      dispatch(addToExpandedConceptIds(concept.conceptId));
    } else {
      dispatch(removeFromExpandedConceptIds(concept.conceptId));
    }
    dispatch(clearEditAnnotationError());
    dispatch(clearCreateAnnotationError());
    // When user manually toggles, clear the "all expanded" state since it's no longer synchronized
    dispatch(clearAreAllConceptsExpanded());
  };

  useEffect(() => {
    // Only sync when areAllConceptsExpanded is explicitly true or false, not undefined
    if (areAllConceptsExpanded === true) {
      dispatch(addToExpandedConceptIds(concept.conceptId));
    } else if (areAllConceptsExpanded === false) {
      dispatch(removeFromExpandedConceptIds(concept.conceptId));
    }
    // If undefined, do nothing - let individual card state persist
  }, [areAllConceptsExpanded, concept.conceptId, dispatch]);

  const conceptAnnotationIdsInEditMode = useAppSelector(selectConceptAnnotationIdsInEditMode);

  const getAnnotationCountSaved = useMemo(() => {
    const thisConceptAnnotationIds = conceptAnnotationIdsInEditMode.filter((id) =>
      id.startsWith(concept.conceptId)
    );
    return annotationCount - thisConceptAnnotationIds.length;
  }, [conceptAnnotationIdsInEditMode, annotationCount]);

  return {
    getAnnotationCountSaved,
    isExpanded,
    handleSetIsExpanded,
  };
};
